import { Button, Combobox, Search } from '@ibsys/kit';
import { SearchIcon } from 'lucide-react';
import { ChangeEvent, FC, KeyboardEvent, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PhraseSearchRole } from '@/entities/search';
import { cn } from '@/shared/lib';
import { CommentAuthorSelect } from './CommentAuthorSelect';
import { CONTENT_SEARCH_SELECT_WIDTH_CLASS } from './constants';
import {
  ACTIONS_BLOCK_CLASS,
  ACTIONS_EXPANDED_BLOCK_CLASS,
  CONTROL_BLOCK_HEIGHT_CLASS,
  CONTROL_ROW_CLASS,
  PRESETS_BLOCK_CLASS,
  PRESETS_PLATED_START_CLASS,
  SEARCH_EXPANDED_BLOCK_CLASS,
  SEARCH_TOGGLES_BLOCK_CLASS,
} from './controlRowClasses';
import { OperatorSelect } from './OperatorSelect';

/** Значение выпадающего списка говорящего «Все» — роль в запрос не передаётся (обе стороны). */
const PHRASE_SEARCH_ROLE_ALL = 'all';

/** Запас справа, чтобы длинный запрос не уходил под кнопки в конце поля; пересчитать при изменении их состава. */
const SEARCH_INPUT_TRAILING_SPACE_CLASS = 'pr-20!';

/**
 * Активный таб панели поиска по содержимому диалога.
 * `'attribute'` — поиск по атрибутам (SmartSearchInput);
 * `'phrase'`/`'comment'` — контентный поиск;
 * `null` — ни одна вкладка не активна, инпут скрыт.
 */
export type ContentSearchTab = 'attribute' | 'phrase' | 'comment';

/**
 * Пропсы общей панели режимов поиска по содержимому диалога
 * (spec: 03 IL/Переключатели поиска по содержимому диалога).
 *
 * Панель управляет четырьмя взаимоисключающими состояниями: поиском по атрибутам
 * (`'attribute'`), по репликам (`'phrase'`), по комментариям (`'comment'`) и
 * полностью свёрнутым состоянием (`null`). Клик по уже активной вкладке деактивирует
 * поиск — `onTabChange` вызывается с `null`.
 */
export type ContentSearchPanelProps = {
  /** Активный таб поиска или `null` (поиск свёрнут) */
  activeTab: ContentSearchTab | null;
  /** Текущее значение инпута активного контентного режима (phrase/comment) до подтверждения */
  inputValue: string;
  /** Изменение значения инпута до подтверждения */
  onInputChange: (value: string) => void;
  /**
   * Очистка поля кнопкой-крестиком внутри поля. Сбрасывает поиск текущего режима целиком
   * (введённый и применённый фрагмент, донастройку запроса к «Все»), не сворачивая панель.
   */
  onClearInput: () => void;
  /** Переключение таба. Передача того же таба, что и активный, деактивирует поиск (передаётся `null`). */
  onTabChange: (tab: ContentSearchTab | null) => void;
  /**
   * Подтверждение поиска (Enter или кнопка). Получает явное значение,
   * т.к. @ibsys/kit Search коммитит value через onChange только по blur — в момент
   * нажатия Enter родительский inputValue ещё не обновлён.
   */
  onSubmit: (value: string) => void;
  /** Выбранная сторона говорящего в режиме «по репликам»; `undefined` — «Все» (обе стороны) */
  phraseSearchRole?: PhraseSearchRole;
  /** Смена стороны говорящего — управляющее действие уровня запроса (список перечитывается) */
  onPhraseSearchRoleChange?: (role: PhraseSearchRole | undefined) => void;
  /** Выбранные операторы в режиме «по репликам»; пустой массив — «Все» (все консультанты) */
  phraseSearchEmployeeIds?: number[];
  /** Смена набора операторов — управляющее действие уровня запроса (список перечитывается) */
  onPhraseSearchEmployeeIdsChange?: (employeeIds: number[]) => void;
  /** Выбранные авторы в режиме «по комментариям»; пустой массив — «Все» (все авторы) */
  commentAuthorIds?: number[];
  /** Смена набора авторов комментариев — управляющее действие уровня запроса (список перечитывается) */
  onCommentAuthorIdsChange?: (authorIds: number[]) => void;
  /** Дополнительные элементы в левой части строки вкладок (например, кнопка «Действия») */
  leadingSlot?: ReactNode;
  /** Строка, раскрываемая левым слотом (например, блок массовых действий) */
  leadingExpandedSlot?: ReactNode;
  /**
   * Строка, раскрываемая переключателями поиска извне (поиск по атрибутам). Внутренние режимы
   * (по репликам, по комментариям) раскрывают собственное поле и этот слот не используют.
   */
  searchExpandedSlot?: ReactNode;
  /** Дополнительные элементы в правой части строки вкладок (например, кнопка пресетов) */
  trailingSlot?: ReactNode;
  /**
   * Правый слот начинается с плашки (кнопки с рамкой или заливкой), а не со значка — блок
   * сдвигается, чтобы край плашки встал в колонку значков соседних блоков.
   */
  isTrailingStartPlated?: boolean;
  className?: string;
};

export const ContentSearchPanel: FC<ContentSearchPanelProps> = ({
  activeTab,
  inputValue,
  onInputChange,
  onClearInput,
  onTabChange,
  onSubmit,
  phraseSearchRole,
  onPhraseSearchRoleChange,
  phraseSearchEmployeeIds,
  onPhraseSearchEmployeeIdsChange,
  commentAuthorIds,
  onCommentAuthorIdsChange,
  leadingSlot,
  leadingExpandedSlot,
  searchExpandedSlot,
  trailingSlot,
  isTrailingStartPlated,
  className,
}) => {
  const { t } = useTranslation(['dialogs', 'common']);

  // Варианты говорящего: «Все» (роль не передаётся) / «Клиент» / «Консультант».
  const roleOptions = useMemo(
    () => [
      { value: PHRASE_SEARCH_ROLE_ALL, label: t('dialogs:phraseSearch.role.all') },
      { value: 'client', label: t('dialogs:phraseSearch.role.client') },
      { value: 'employee', label: t('dialogs:phraseSearch.role.employee') },
    ],
    [t],
  );

  const handleRoleChange = useCallback(
    (value?: string | number) => {
      onPhraseSearchRoleChange?.(
        value == null || value === PHRASE_SEARCH_ROLE_ALL ? undefined : (value as PhraseSearchRole),
      );
    },
    [onPhraseSearchRoleChange],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onInputChange(e.target.value);
    },
    [onInputChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
      // Search коммитит value во внешний onChange по blur; на keyDown inputValue ещё stale.
      // Берём актуальное значение прямо из target и синхронизируем родительский стейт.
      const currentValue = e.currentTarget.value;
      onInputChange(currentValue);
      onSubmit(currentValue);
    },
    [onInputChange, onSubmit],
  );

  const handleSubmitClick = useCallback(() => {
    onSubmit(inputValue);
  }, [inputValue, onSubmit]);

  // Клик по активному табу — деактивация (передаём null); по неактивному — переключение на него.
  const handleTabClick = useCallback(
    (tab: ContentSearchTab) => {
      onTabChange(activeTab === tab ? null : tab);
    },
    [activeTab, onTabChange],
  );

  const handleAttributeClick = useCallback(() => {
    handleTabClick('attribute');
  }, [handleTabClick]);

  const handlePhraseClick = useCallback(() => {
    handleTabClick('phrase');
  }, [handleTabClick]);

  const handleCommentClick = useCallback(() => {
    handleTabClick('comment');
  }, [handleTabClick]);

  const placeholder =
    activeTab === 'comment'
      ? t('dialogs:commentSearch.placeholder')
      : t('dialogs:phraseSearch.placeholder');

  const isContentInputVisible = activeTab === 'phrase' || activeTab === 'comment';

  return (
    <div className={cn('@container/dialogs-control', className)}>
      <div className={CONTROL_ROW_CLASS}>
        {trailingSlot && (
          <div
            className={cn(
              PRESETS_BLOCK_CLASS,
              CONTROL_BLOCK_HEIGHT_CLASS,
              isTrailingStartPlated && PRESETS_PLATED_START_CLASS,
            )}
          >
            {trailingSlot}
          </div>
        )}

        <div className={cn(SEARCH_TOGGLES_BLOCK_CLASS, CONTROL_BLOCK_HEIGHT_CLASS)}>
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <SearchIcon className="size-4 shrink-0" />
            {t('dialogs:contentSearch.label')}
          </span>
          <Button
            variant={activeTab === 'attribute' ? 'secondary' : 'ghost'}
            size="sm"
            aria-pressed={activeTab === 'attribute'}
            onClick={handleAttributeClick}
            className="shrink-0"
          >
            {t('dialogs:attributeSearch.toggle')}
          </Button>
          <Button
            variant={activeTab === 'phrase' ? 'secondary' : 'ghost'}
            size="sm"
            aria-pressed={activeTab === 'phrase'}
            onClick={handlePhraseClick}
            className="shrink-0"
          >
            {t('dialogs:phraseSearch.toggle')}
          </Button>
          <Button
            variant={activeTab === 'comment' ? 'secondary' : 'ghost'}
            size="sm"
            aria-pressed={activeTab === 'comment'}
            onClick={handleCommentClick}
            className="shrink-0"
          >
            {t('dialogs:commentSearch.toggle')}
          </Button>
        </div>

        {/* На телефоне поле ввода встаёт строкой ниже донастроек: в одной строке с ними
            оно сжимается до нечитаемого */}
        {isContentInputVisible && (
          <div
            className={cn(
              SEARCH_EXPANDED_BLOCK_CLASS,
              'flex flex-row items-center gap-2 max-sm:flex-col max-sm:items-stretch',
            )}
          >
            {activeTab === 'phrase' && (
              <Combobox
                aria-label={t('dialogs:phraseSearch.role.label')}
                value={phraseSearchRole ?? PHRASE_SEARCH_ROLE_ALL}
                onChange={handleRoleChange}
                options={roleOptions}
                isSearchable={false}
                className={CONTENT_SEARCH_SELECT_WIDTH_CLASS}
              />
            )}
            {activeTab === 'phrase' &&
              phraseSearchRole === 'employee' &&
              onPhraseSearchEmployeeIdsChange && (
                <OperatorSelect
                  value={phraseSearchEmployeeIds ?? []}
                  onChange={onPhraseSearchEmployeeIdsChange}
                  className={CONTENT_SEARCH_SELECT_WIDTH_CLASS}
                />
              )}
            {activeTab === 'comment' && onCommentAuthorIdsChange && (
              <CommentAuthorSelect
                value={commentAuthorIds ?? []}
                onChange={onCommentAuthorIdsChange}
                className={CONTENT_SEARCH_SELECT_WIDTH_CLASS}
              />
            )}
            <div className="flex-1 min-w-0">
              <Search
                value={inputValue}
                onChange={handleInputChange}
                onClear={onClearInput}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                classNames={{
                  input: SEARCH_INPUT_TRAILING_SPACE_CLASS,
                }}
                iconRight={
                  <Button
                    tabIndex={-1}
                    variant="ghost"
                    size="sm"
                    disabled={!inputValue.trim()}
                    icon={<SearchIcon />}
                    onClick={handleSubmitClick}
                    aria-label={t('common:search')}
                  />
                }
              />
            </div>
          </div>
        )}

        {searchExpandedSlot && <div className={SEARCH_EXPANDED_BLOCK_CLASS}>{searchExpandedSlot}</div>}

        {leadingSlot && (
          <div className={cn(ACTIONS_BLOCK_CLASS, CONTROL_BLOCK_HEIGHT_CLASS)}>{leadingSlot}</div>
        )}

        {leadingExpandedSlot && <div className={ACTIONS_EXPANDED_BLOCK_CLASS}>{leadingExpandedSlot}</div>}
      </div>
    </div>
  );
};
